import { z } from 'zod';
// Patient Info Schema
export const patientInfoSchema = z.object({
    age: z.number().min(0).max(150).optional(),
    gender: z.enum(['male', 'female', 'other']).optional(),
    canton: z.string().optional(),
    postalCode: z.string().optional(),
});
// Question Schema
export const questionSchema = z.object({
    id: z.string(),
    category: z.string(),
    question: z.string(),
    type: z.enum(['text', 'single_choice', 'multiple_choice', 'scale', 'boolean']),
    options: z.array(z.string()).optional(),
    required: z.boolean().default(true),
});
// Questionnaire Response
export const questionnaireResponseSchema = z.object({
    specialist_role: z.string(),
    recommended_specialties: z.array(z.string()),
    questions: z.array(questionSchema),
});
// Question Answer
export const questionAnswerSchema = z.object({
    id: z.string(),
    question: z.string(),
    answer: z.string(),
});
// Indication Response
export const indicationIssueSchema = z.object({
    severity: z.enum(['critical', 'warning', 'info']),
    description: z.string(),
});
export const indicationResponseSchema = z.object({
    indication_appropriate: z.boolean(),
    patient_expectations: z.object({
        level: z.enum(['realistic', 'optimistic', 'unrealistic']),
        assessment: z.string(),
    }),
    communication_needs: z.array(z.string()),
    provider_traits_needed: z.array(z.string()),
    issues: z.array(indicationIssueSchema),
    criteria_adjustments: z.array(z.string()),
});
// Criteria Response
export const criterionSchema = z.object({
    name: z.string(),
    importance: z.enum(['critical', 'high', 'medium', 'low']),
    description: z.string(),
});
export const searchParametersSchema = z.object({
    specialties: z.array(z.string()),
    min_experience_years: z.number(),
    max_distance_km: z.number(),
    required_capabilities: z.array(z.string()),
});
export const criteriaResponseSchema = z.object({
    criteria: z.array(criterionSchema),
    search_parameters: searchParametersSchema,
    ideal_provider_profile: z.string(),
});
// Case Status
export const caseStatusSchema = z.enum(['draft', 'in_review', 'completed', 'archived']);
// Full Case Schema
export const caseSchema = z.object({
    id: z.string(),
    title: z.string(),
    condition: z.string(),
    patientInfo: patientInfoSchema,
    questionnaire: z.array(questionAnswerSchema).default([]),
    indication: indicationResponseSchema.optional(),
    criteria: criteriaResponseSchema.optional(),
    status: caseStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    createdBy: z.object({
        id: z.string(),
        name: z.string(),
        email: z.string(),
    }),
});
// Match Result
export const matchResultSchema = z.object({
    id: z.string(),
    doctorId: z.string(),
    doctorName: z.string(),
    specialty: z.string(),
    score: z.number(),
    reasoning: z.string(),
    matchedCriteria: z.array(z.string()),
});
// Swiss Cantons
export const SWISS_CANTONS = [
    { code: 'AG', name: 'Aargau' },
    { code: 'AI', name: 'Appenzell Innerrhoden' },
    { code: 'AR', name: 'Appenzell Ausserrhoden' },
    { code: 'BE', name: 'Bern' },
    { code: 'BL', name: 'Basel-Landschaft' },
    { code: 'BS', name: 'Basel-Stadt' },
    { code: 'FR', name: 'Fribourg' },
    { code: 'GE', name: 'Genève' },
    { code: 'GL', name: 'Glarus' },
    { code: 'GR', name: 'Graubünden' },
    { code: 'JU', name: 'Jura' },
    { code: 'LU', name: 'Luzern' },
    { code: 'NE', name: 'Neuchâtel' },
    { code: 'NW', name: 'Nidwalden' },
    { code: 'OW', name: 'Obwalden' },
    { code: 'SG', name: 'St. Gallen' },
    { code: 'SH', name: 'Schaffhausen' },
    { code: 'SO', name: 'Solothurn' },
    { code: 'SZ', name: 'Schwyz' },
    { code: 'TG', name: 'Thurgau' },
    { code: 'TI', name: 'Ticino' },
    { code: 'UR', name: 'Uri' },
    { code: 'VD', name: 'Vaud' },
    { code: 'VS', name: 'Valais' },
    { code: 'ZG', name: 'Zug' },
    { code: 'ZH', name: 'Zürich' },
];
// Export Swiss Postal Codes data
export { SWISS_POSTAL_CODES, getPostalCodeCoordinates, getPostalCodeData, getCityCoordinates, getPostalCodeByCity, } from './swissPostalCodes';
